import { MouseEventHandler, PropsWithChildren, useCallback, useEffect } from "react";
import { transitionClassName } from "./commonClassNames";

export type ModalProps = {
  isActive: boolean;
  setIsActive: (isActive: boolean) => void;
  onDismiss?: () => void;
};

export const Modal: React.FC<PropsWithChildren<ModalProps>> = ({ children, onDismiss, isActive, setIsActive }) => {
  const closeModal = useCallback(() => {
    setIsActive(false);
    onDismiss?.();
  }, [onDismiss, setIsActive]);

  const onClickBackground = useCallback<MouseEventHandler<HTMLDivElement>>(
    (event) => {
      event.stopPropagation();
      closeModal();
    },
    [closeModal],
  );

  const containerClassName = ["fixed z-10 inset-0 overflow-y-auto", isActive ? "" : "pointer-events-none"].join(" ");

  const backgroundOverlayClassName = [
    "fixed inset-0 bg-neutral-800 bg-opacity-75",
    transitionClassName({ opacity: true }),
    isActive ? "opacity-100" : "pointer-events-none opacity-0",
  ].join(" ");

  const contentClassName = [
    "transform",
    transitionClassName({ all: true }),
    isActive ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-4",
  ].join(" ");

  useEffect(() => {
    function keyListener(event: KeyboardEvent) {
      switch (event.code) {
        case "Escape": {
          closeModal();
          break;
        }
        default: {
          break;
        }
      }
    }

    if (isActive) {
      window.addEventListener("keydown", keyListener);
    } else {
      window.removeEventListener("keydown", keyListener);
    }

    return () => {
      window.removeEventListener("keypress", keyListener);
    };
  }, [closeModal, isActive]);

  return (
    <div className={containerClassName} role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen">
        <div className={backgroundOverlayClassName} onClick={onClickBackground} aria-hidden="true" />

        <div className={contentClassName}>{children}</div>
      </div>
    </div>
  );
};
